<script lang="ts">
  import { cn } from "@/lib/utils";
  import { tweened } from "svelte/motion";
  import { cubicOut } from "svelte/easing";

  export let ftpWatts: number;     // Highest TP (FTP)
  export let maxPowerWatts: number; // Highest power recorded
  export let revealed: boolean = false;
  export let color: string = '#FFD166'; // lime accent

  const tidy = (n: number) => new Intl.NumberFormat().format(Math.round(n ?? 0));

  // Accent RGB for alpha gradients
  function hexToRgb(hex: string): string {
    const m = hex.replace('#','').match(/.{1,2}/g);
    if (!m || m.length < 3) return '117,196,31'; // fallback for #75C41F
    const [r,g,b] = m.map(v => parseInt(v, 16));
    return `${r},${g},${b}`;
  }
  $: accentRgb = hexToRgb(color);

  // Count-up stores for subtle reveal animation
  const ftpDisplay = tweened(0, { duration: 800, easing: cubicOut });
  const maxDisplay = tweened(0, { duration: 900, easing: cubicOut });
  $: if (revealed) {
    ftpDisplay.set(Math.max(0, ftpWatts ?? 0));
    maxDisplay.set(Math.max(0, maxPowerWatts ?? 0));
  } else {
    // Reset displays when not revealed to mimic RecordsStrainCard behavior
    ftpDisplay.set(0);
    maxDisplay.set(0);
  }

  // Dial visualization progress
  $: safeMax = Math.max(1, maxPowerWatts ?? 1);
  $: ftpProgress = Math.max(0, Math.min(1, (ftpWatts ?? 0) / safeMax));
  $: maxProgress = Math.max(0, Math.min(1, (maxPowerWatts ?? 0) / (maxPowerWatts ?? 1))); // always 1 if max present
  // Gate meter progress to reveal (so ring sweeps when visible)
  $: ftpMeterProgress = revealed ? ftpProgress : 0;
  $: maxMeterProgress = revealed ? maxProgress : 0;

  // SVG ring metrics
  const R_OUT = 54;
  const R_IN  = 40;
  const C_OUT = 2 * Math.PI * R_OUT;
  const C_IN  = 2 * Math.PI * R_IN;

  // Equalizer bars configuration (accent-colored)
  const eqBars: number[] = [0.25, 0.55, 0.9, 0.45, 0.7, 0.35, 0.8, 0.6, 0.3];
</script>

<div class='w-full rounded-2xl p-[1px] power-card-shell {revealed ? "revealed" : ""}'
  style={`--accent:${color}; --accent-rgb:${accentRgb}; box-shadow: 0 10px 28px rgba(0,0,0,0.35), inset 0 0 8px rgba(255,255,255,0.05);`}>
  <div class='rounded-2xl power-card-inner text-off-white'>
    <div class='w-full flex flex-col gap-5 p-5 overflow-hidden'>
      <div class='flex flex-row items-center gap-3 header'>
        <i class='fa-light fa-bolt fa-2xl bolt' aria-hidden='true'></i>
        <span class='text-xl font-bold tracking-wide'>Power Records</span>
      </div>

      <!-- Hero: Arcade Dial -->
      <div class={cn('dial', revealed ? 'dial-in' : '')}>
        <svg class='dial-svg' viewBox='0 0 140 140' width='140' height='140' aria-hidden='true'>
          <!-- Outer track -->
          <circle cx='70' cy='70' r={R_OUT} class='track' />
          <!-- Outer meter (max power) -->
          <circle cx='70' cy='70' r={R_OUT}
            class='meter meter-max'
            stroke-dasharray={C_OUT}
            stroke-dashoffset={Math.round(C_OUT * (1 - maxMeterProgress))} />
          <!-- Inner track -->
          <circle cx='70' cy='70' r={R_IN} class='track' />
          <!-- Inner meter (FTP) -->
          <circle cx='70' cy='70' r={R_IN}
            class='meter meter-ftp'
            stroke-dasharray={C_IN}
            stroke-dashoffset={Math.round(C_IN * (1 - ftpMeterProgress))} />
        </svg>
        <div class='dial-center'>
          <i class='fa-light fa-bolt' aria-hidden='true'></i>
        </div>
      </div>

      <!-- Accent equalizer bars -->
      <div class={cn('eq', revealed ? 'eq-in' : '')} aria-hidden='true'>
        {#each eqBars as h, i}
          <span class='bar' style={`--h:${h}; --i:${i};`}></span>
        {/each}
      </div>

      <!-- Stats: subtle count-up, micro underline + arcs -->
      <div class='flex flex-col gap-4'>
        <div class={cn('stat-line left', revealed ? 'line-in-1' : '')}>
          <i class='fa-light fa-gauge' aria-hidden='true'></i>
          <div class='text'>
            <span class='value whitespace-nowrap'>{tidy($ftpDisplay)} W</span>
            <span class='label'>Highest TP (FTP)</span>
          </div>
        </div>
        <div class={cn('stat-line right', revealed ? 'line-in-2' : '')}>
          <i class='fa-light fa-bolt' aria-hidden='true'></i>
          <div class='text'>
            <span class='value whitespace-nowrap'>{tidy($maxDisplay)} W</span>
            <span class='label'>Highest Power Recorded</span>
          </div>
        </div>
      </div>

      <div class={cn('caption', revealed ? 'caption-in' : '')}>
        <i class='fa-regular fa-star' aria-hidden='true'></i>
        <span class='text-sm'>Peak moments, peak watts.</span>
      </div>
    </div>
  </div>
  <!-- Gentle looping shimmer overlay, reveal-gated -->
  <div class={cn('shimmer', revealed ? 'shimmer-on' : '')} aria-hidden='true'></div>
</div>

<style>
  .power-card-shell {
    /* Arcade background: grid + stripes + accent glow */
    background:
      linear-gradient(180deg, rgba(12,14,16,0.95), rgba(8,10,12,0.98)),
      radial-gradient(120% 80% at 0% 0%, rgba(var(--accent-rgb),0.08), transparent 60%),
      radial-gradient(100% 90% at 100% 100%, rgba(155,93,229,0.06), transparent 55%),
      repeating-linear-gradient(45deg, rgba(var(--accent-rgb),0.05) 0 8px, rgba(0,0,0,0) 8px 16px);
    position: relative;
    overflow: hidden;
  }
  .power-card-inner {
    background:
      radial-gradient(circle at 24% 18%, rgba(255,255,255,0.06), transparent 40%),
      radial-gradient(circle at 76% 88%, rgba(255,255,255,0.04), transparent 40%),
      linear-gradient(180deg, rgba(18,20,22,0.94), rgba(8,9,10,0.98));
    border-radius: 1rem;
    position: relative;
  }
  /* Background glow pulse on reveal (0–200ms) */
  .power-card-inner::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(80% 50% at 50% 0%, rgba(var(--accent-rgb),0.0), rgba(var(--accent-rgb),0.15) 60%, rgba(var(--accent-rgb),0.0) 90%);
    opacity: 0;
    pointer-events: none;
  }
  .revealed .power-card-inner::before { animation: bgfade 220ms ease-out forwards; }
  @keyframes bgfade { to { opacity: 1; } }

  .header i.bolt { color: var(--accent); text-shadow: 0 0 22px rgba(var(--accent-rgb),0.55); opacity: 0.9; }
  .revealed .header i.bolt { animation: glowPulse 900ms ease-in-out 200ms 1; }
  @keyframes glowPulse { 0% { filter: drop-shadow(0 0 0 rgba(var(--accent-rgb),0)); }
    50% { filter: drop-shadow(0 0 16px rgba(var(--accent-rgb),0.65)); }
    100% { filter: drop-shadow(0 0 0 rgba(var(--accent-rgb),0)); } }

  /* Dial visualization */
  .dial { position: relative; height: 140px; display: grid; place-items: center; opacity: 0; transform: scale(0.0); }
  .dial-in { animation: dial-in 700ms 200ms ease-out forwards; }
  @keyframes dial-in { 0% { opacity: 0; transform: scale(0); } 60% { opacity: 1; transform: scale(1.06);} 100% { opacity: 1; transform: scale(1);} }
  .dial-svg { filter: drop-shadow(0 6px 18px rgba(0,0,0,0.45)); }
  .track { fill: none; stroke: rgba(255,255,255,0.08); stroke-width: 6; }
  .meter { fill: none; stroke-linecap: round; transition: stroke-dashoffset 900ms cubic-bezier(0.22,0.72,0.25,1); }
  .meter-max { stroke: var(--accent); stroke-width: 6; filter: drop-shadow(0 0 10px rgba(var(--accent-rgb),0.35)); }
  .meter-ftp { stroke: rgba(255,255,255,0.85); stroke-width: 5; filter: drop-shadow(0 0 6px rgba(255,255,255,0.25)); }
  .dial-center { position: absolute; width: 56px; height: 56px; border-radius: 999px; display: grid; place-items: center; border: 1px solid rgba(var(--accent-rgb),0.35); background: rgba(var(--accent-rgb),0.12); box-shadow: 0 0 18px rgba(var(--accent-rgb),0.22); }
  .dial-center i { color: #cfd8dc; text-shadow: 0 0 8px rgba(255,255,255,0.08); }

  /* Equalizer bars */
  .eq { display: grid; grid-template-columns: repeat(9, 1fr); gap: 6px; height: 40px; margin-top: -4px; opacity: 0; }
  .eq-in { animation: eqfade 200ms 200ms ease-out forwards; }
  @keyframes eqfade { to { opacity: 1; } }
  .eq .bar { align-self: end; width: 100%; height: 0; border-radius: 3px; background: linear-gradient(180deg, rgba(var(--accent-rgb),0.9), rgba(var(--accent-rgb),0.5)); box-shadow: 0 0 8px rgba(var(--accent-rgb),0.35); }
  .revealed .eq .bar { animation: bargrow 600ms cubic-bezier(0.22,0.72,0.25,1) forwards; animation-delay: calc(200ms + var(--i) * 60ms); }
  @keyframes bargrow { to { height: calc(var(--h) * 100%); } }

  /* Stat lines with underlines and mini arcs */
  .stat-line { position: relative; display: flex; align-items: center; gap: 10px; opacity: 0; transform: translateY(12px); }
  .stat-line i { color: #cfd8dc; text-shadow: 0 0 8px rgba(255,255,255,0.08); }
  .stat-line .text { display: flex; align-items: baseline; gap: 8px; }
  .stat-line .value { font-size: 1.3rem; font-weight: 800; }
  .stat-line .label { font-size: 0.85rem; opacity: 0.85; }
  .stat-line::after { content: ""; position: absolute; left: 0; right: 0; bottom: -2px; height: 3px; background: linear-gradient(90deg, rgba(var(--accent-rgb),0.0), rgba(var(--accent-rgb),0.35), rgba(var(--accent-rgb),0.0)); transform: scaleX(0); transform-origin: left; filter: blur(0.6px); }
  .stat-line.left::after { left: 0; right: 40%; }
  .stat-line.right::after { left: 40%; right: 0; }
  .line-in-1, .line-in-2 { animation: stat-in 420ms ease-out forwards; }
  .line-in-1 { animation-delay: 450ms; }
  .line-in-2 { animation-delay: 600ms; }
  @keyframes stat-in { 0% { opacity: 0; transform: translateY(12px); } 100%{ opacity: 1; transform: translateY(0); } }
  .line-in-1::after, .line-in-2::after { animation: underline-in 720ms ease-out forwards; }
  @keyframes underline-in { 0% { transform: scaleX(0); } 100%{ transform: scaleX(1); } }

  /* Mini arcs removed as requested */

  .caption { display: flex; align-items: center; gap: 8px; opacity: 0; transform: translateY(10px); }
  .caption-in { animation: caption-in 420ms 950ms ease-out forwards; }
  @keyframes caption-in { 0% { opacity: 0; transform: translateY(10px); } 100%{ opacity: 1; transform: translateY(0); } }

  /* Gentle looping shimmer: oversized beam + edge mask for smoother blending */
  .shimmer { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
  .shimmer::before {
    content: "";
    position: absolute;
    top: -20%; left: -60%;
    width: 220%; height: 140%;
    opacity: 0;
    /* narrow bright core with blurred edges */
    background: linear-gradient(120deg, rgba(255,255,255,0) 45%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0) 55%);
    filter: blur(10px);
    transform: translateX(-30%);
    /* fade out at container edges */
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%);
    mask-image: linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%);
  }
  .shimmer-on::before { animation: shimmer-beam 4.5s ease-in-out 1200ms infinite; }
  @keyframes shimmer-beam {
    0%   { opacity: 0.0; transform: translateX(-30%); }
    15%  { opacity: 0.28; }
    50%  { opacity: 0.14; transform: translateX(30%); }
    85%  { opacity: 0.28; }
    100% { opacity: 0.0; transform: translateX(90%); }
  }
</style>
